import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, X } from "lucide-react";
import { OffersList, OffersLockedPanel, RestaurantCard } from "@/components/restaurant";
import { useTranslation } from "@/i18n/I18nProvider";
import type { EventSurface } from "@/lib/analytics";
import { cityName } from "@/lib/cities";
import type { Discount, Restaurant, RestaurantId } from "@/types/restaurant";
import type { HomeTab } from "./HomeTabs";

// The feed's responsive layout lives here, in ONE place, so a future variant
// (horizontal scroll, 2-col phone, etc.) is a single-line change. Used by both
// the loading skeleton and the real grid so they always stay in lockstep.
// `grid-cols-1` (= minmax(0,1fr)) is load-bearing: without an explicit base
// column the phone grid falls back to an implicit AUTO column that grows to the
// card's min-content and overflows the container to the right (eating the right
// gutter, so the feed looks shifted left). The 1fr track clamps each card to the
// container width, keeping the left/right gutters equal.
const FEED_GRID_CLASS = "mt-6 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3";

/**
 * The feed body under the strips and filters: skeletons, the load error,
 * the Offers tab (or its guest lock), the empty-city and no-match states, and
 * the venue grid itself.
 */
export function FeedGrid({
  city,
  tab,
  signedIn,
  loading,
  loadError,
  onRetry,
  restaurants,
  orderedCount,
  visibleRestaurants,
  discountsByRestaurant,
  now,
  hasFilters,
  onClearFilters,
  onSelect,
  onSignUp,
}: {
  city: string;
  tab: HomeTab;
  signedIn: boolean;
  loading: boolean;
  loadError: boolean;
  onRetry: () => void;
  restaurants: Restaurant[];
  orderedCount: number;
  visibleRestaurants: Array<{ r: Restaurant; open: boolean }>;
  discountsByRestaurant: Record<RestaurantId, Discount[]>;
  now: Date;
  hasFilters: boolean;
  onClearFilters: () => void;
  onSelect: (r: Restaurant, surface: EventSurface) => void;
  onSignUp: () => void;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return loading ? (
    <div className={FEED_GRID_CLASS}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-72 animate-pulse rounded-2xl border border-white/10 light:border-black/10 bg-zinc-900/50 light:bg-white/60"
        />
      ))}
    </div>
  ) : loadError ? (
    <div className="mt-10 rounded-2xl border border-dashed border-white/15 light:border-black/15 bg-zinc-900/30 light:bg-white/50 p-10 text-center">
      <p className="text-sm text-zinc-300 light:text-zinc-700">{t("HOME_LOAD_ERROR")}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex items-center justify-center rounded-full bg-paper light:bg-ink px-4 py-2 text-sm font-semibold text-ink light:text-paper transition hover:bg-white light:hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
      >
        {t("RETRY")}
      </button>
    </div>
  ) : tab === "offers" ? (
    signedIn ? (
      <OffersList
        restaurants={restaurants}
        discountsByRestaurant={discountsByRestaurant}
        now={now}
        cityLabel={cityName(city)}
        onSelectRestaurant={(r) => onSelect(r, "offers")}
      />
    ) : (
      <OffersLockedPanel cityLabel={cityName(city)} onSignUp={onSignUp} />
    )
  ) : orderedCount === 0 ? (
    <div className="mt-10 rounded-2xl border border-dashed border-white/15 light:border-black/15 bg-zinc-900/30 light:bg-white/50 p-10 text-center">
      <p className="text-sm text-zinc-300 light:text-zinc-700">{t("HOME_CITY_EMPTY", { city: cityName(city) })}</p>
      <Link
        to="/"
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-200 light:text-zinc-800 hover:text-white light:hover:text-zinc-950"
      >
        <MapPin className="h-4 w-4" aria-hidden />
        {t("HOME_CHANGE_CITY")}
      </Link>
    </div>
  ) : visibleRestaurants.length === 0 ? (
    <div className="mt-6 rounded-2xl border border-dashed border-white/15 light:border-black/15 bg-zinc-900/30 light:bg-white/50 p-10 text-center sm:mt-8">
      <p className="text-sm text-zinc-300 light:text-zinc-700">{t("HOME_FILTERS_NONE")}</p>
      {hasFilters && (
        <button
          type="button"
          onClick={onClearFilters}
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-200 light:text-zinc-800 transition hover:text-white light:hover:text-zinc-950"
        >
          <X className="h-4 w-4" aria-hidden />
          {t("HOME_FILTERS_CLEAR")}
        </button>
      )}
    </div>
  ) : (
    <motion.div layout className={FEED_GRID_CLASS}>
      {/* Opacity-only stagger: a y-slide composites each card while
          its image decodes and flashes a fringe at the image edge. */}
      {visibleRestaurants.map(({ r, open }, i) => (
        <motion.div
          key={r.id}
          layout={!reduceMotion}
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 320,
            damping: 32,
            opacity: { duration: 0.45, delay: Math.min(i * 0.04, 0.36) },
          }}
          className="h-full"
        >
          <RestaurantCard
            restaurant={r}
            isOpen={open}
            discounts={discountsByRestaurant[r.id]}
            onSelect={() => onSelect(r, "feed")}
          />
        </motion.div>
      ))}
    </motion.div>
  );
}
